"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Image from "next/image";
import { MotionConfig } from "framer-motion";
import { useQuery } from "convex/react";
import { Minus, Plus, Loader2, Sparkles, XIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  formatPrice,
  type CartItem,
  type CartItemModifier,
} from "@/stores/cart-store";
import { useActiveCartStore } from "@/components/order/cart-scope";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import {
  getTemplateForItem,
  PROTEIN_OPTIONS,
  SAUCE_OPTIONS,
  CRUST_OPTIONS,
  PIZZA_TOPPING_OPTIONS,
  getPizzaToppingPriceForSize,
  DRESSING_OPTIONS,
  BRUNCH_ADDONS,
} from "@/lib/order-config";
import {
  formatMenuItemPriceLabel,
  getDefaultPizzaSizeValue,
  getPizzaSizeOptionsFromPriceText,
} from "@/lib/menu-price-options";
import { ModifierGroup } from "./modifier-group";
import {
  TemperatureSelector,
  PastaCustomizer,
  PizzaCustomizer,
  SaladCustomizer,
  BrunchCustomizer,
} from "./customizers";
import { GoesWellWithSection } from "./goes-well-with-section";
import { api } from "../../../convex/_generated/api";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";
import type { Doc, Id } from "../../../convex/_generated/dataModel";

// Type for modifier options (matches Convex schema)
interface ModifierOption {
  name: string;
  priceAdjustment: number;
  isDefault?: boolean;
  isAvailable: boolean;
}

// Type for menu modifiers (matches Convex schema)
export interface MenuModifier {
  _id: Id<"menu_modifiers">;
  name: string;
  description?: string;
  selectionType: "single" | "multiple";
  isRequired: boolean;
  minSelections?: number;
  maxSelections?: number;
  options: ModifierOption[];
  sortOrder: number;
}

interface ItemCustomizationSheetProps {
  item: Doc<"menu_items">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingItem?: CartItem | null;
  preview?: {
    modifiers: MenuModifier[];
    onAdd: (item: Omit<CartItem, "id">) => void;
  };
}

const MODIFIER_LOAD_TIMEOUT_MS = 6000;
const EMPTY_MODIFIERS: MenuModifier[] = [];

function getOptionValueFromLabel(
  options: readonly { label: string; value: string }[],
  label: string
) {
  return options.find((option) => option.label === label)?.value ?? null;
}

const handledModifierNamesByTemplate: Record<string, string[]> = {
  temperature: ["Temperature", "Temp"],
  pasta: ["Sauce Amount", "Add Protein", "Protein"],
  pizza: ["Crust", "Crust Preference", "Size", "Topping"],
  salad: ["Dressing", "Add Protein", "Protein"],
};

function isModifierHandledByTemplate(template: string, modifierName: string) {
  const handledNames = handledModifierNamesByTemplate[template] || [];
  const normalizedModifierName = modifierName.toLowerCase();
  return handledNames.some((handledName) =>
    normalizedModifierName.includes(handledName.toLowerCase())
  );
}

export function ItemCustomizationSheet({
  item,
  open,
  onOpenChange,
  editingItem = null,
  preview,
}: ItemCustomizationSheetProps) {
  const [quantity, setQuantity] = useState(1);
  const [selections, setSelections] = useState<Map<string, string[]>>(new Map());
  const [pulsingGroup, setPulsingGroup] = useState<string | null>(null);
  const [blockedMsg, setBlockedMsg] = useState("");
  const [modifierLoadTimedOut, setModifierLoadTimedOut] = useState(false);

  // Refs to scroll/highlight the first unmet required modifier group on a blocked tap.
  const groupRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitLockRef = useRef(false);

  // Category-specific state
  const [temperature, setTemperature] = useState<string | null>(null);
  const [sauceAmount, setSauceAmount] = useState("regular");
  const [crustPreference, setCrustPreference] = useState("regular");
  const [selectedPizzaSize, setSelectedPizzaSize] = useState<string | null>(null);
  const [selectedPizzaToppings, setSelectedPizzaToppings] = useState<string[]>([]);
  const [dressingPreference, setDressingPreference] = useState("regular");
  const [selectedProtein, setSelectedProtein] = useState<string | null>(null);
  const [selectedBrunchAddons, setSelectedBrunchAddons] = useState<string[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState("");

  const useCart = useActiveCartStore();
  const addItem = useCart((state) => state.addItem);
  const replaceItem = useCart((state) => state.replaceItem);

  // Determine template based on category, with item-level overrides for plates
  // where doneness does not follow the category (e.g. Carne Asada in mains).
  const template = getTemplateForItem(item);

  // Fetch modifiers for this item
  const liveModifiers = useQuery(
    api.menuModifiers.getByMenuItem,
    open && !preview ? { menuItemId: item._id } : "skip"
  ) as MenuModifier[] | undefined;
  const modifiers = preview?.modifiers ?? liveModifiers;
  const effectiveModifiers = modifierLoadTimedOut ? EMPTY_MODIFIERS : modifiers;

  useEffect(() => {
    if (!open || modifiers !== undefined) {
      setModifierLoadTimedOut(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setModifierLoadTimedOut(true);
    }, MODIFIER_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [item._id, modifiers, open]);

  const pizzaSizeOptions = useMemo(() => {
    if (template !== "pizza") return [];
    return getPizzaSizeOptionsFromPriceText(item.priceText, item.price);
  }, [item.price, item.priceText, template]);

  // Filter out modifiers that are handled by category customizers
  const filteredModifiers = useMemo(() => {
    if (!effectiveModifiers) return [];

    return effectiveModifiers.filter((mod) => !isModifierHandledByTemplate(template, mod.name));
  }, [effectiveModifiers, template]);

  const isEditing = editingItem?.menuItemId === item._id;

  // Reset state when item changes or sheet opens
  useEffect(() => {
    if (open && effectiveModifiers) {
      setQuantity(isEditing ? editingItem.quantity : 1);
      setPulsingGroup(null);
      setTemperature(null);
      setSauceAmount("regular");
      setCrustPreference("regular");
      setSelectedPizzaSize(
        !isEditing && template === "pizza"
          ? getDefaultPizzaSizeValue(pizzaSizeOptions)
          : null,
      );
      setSelectedPizzaToppings([]);
      setDressingPreference("regular");
      setSelectedProtein(null);
      setSelectedBrunchAddons([]);
      setSpecialInstructions(isEditing ? editingItem?.specialInstructions ?? "" : "");
      submitLockRef.current = false;

      // Start from defaults for add mode, then hydrate the current cart selections for edit mode.
      const initialSelections = new Map<string, string[]>();
      for (const mod of effectiveModifiers) {
        if (isModifierHandledByTemplate(template, mod.name)) {
          continue;
        }
        const defaultOptions = mod.options
          .filter((opt) => opt.isDefault && opt.isAvailable)
          .map((opt) => opt.name);
        if (!isEditing && defaultOptions.length > 0) {
          initialSelections.set(mod._id, defaultOptions);
        }
      }

      if (isEditing) {
        for (const modifier of editingItem.modifiers) {
          switch (modifier.modifierName) {
            case "Temperature":
              setTemperature(modifier.optionName);
              break;
            case "Sauce Amount": {
              const sauceValue = getOptionValueFromLabel(
                SAUCE_OPTIONS,
                modifier.optionName
              );
              if (sauceValue) {
                setSauceAmount(sauceValue);
              }
              break;
            }
            case "Crust Preference": {
              const crustValue = getOptionValueFromLabel(
                CRUST_OPTIONS,
                modifier.optionName
              );
              if (crustValue) {
                setCrustPreference(crustValue);
              }
              break;
            }
            case "Size": {
              const sizeValue = pizzaSizeOptions.find(
                (option) =>
                  option.label === modifier.optionName ||
                  option.value === modifier.optionName,
              )?.value;
              if (sizeValue) {
                setSelectedPizzaSize(sizeValue);
              }
              break;
            }
            case "Toppings": {
              const toppingValue = getOptionValueFromLabel(
                PIZZA_TOPPING_OPTIONS,
                modifier.optionName
              );
              if (toppingValue) {
                setSelectedPizzaToppings((current) =>
                  current.includes(toppingValue)
                    ? current
                    : [...current, toppingValue]
                );
              }
              break;
            }
            case "Dressing": {
              const dressingValue = getOptionValueFromLabel(
                DRESSING_OPTIONS,
                modifier.optionName
              );
              if (dressingValue) {
                setDressingPreference(dressingValue);
              }
              break;
            }
            case "Add Protein": {
              const proteinValue = getOptionValueFromLabel(
                PROTEIN_OPTIONS,
                modifier.optionName
              );
              if (proteinValue) {
                setSelectedProtein(proteinValue);
              }
              break;
            }
            case "Add-on": {
              const addonValue = getOptionValueFromLabel(
                BRUNCH_ADDONS,
                modifier.optionName
              );
              if (addonValue) {
                setSelectedBrunchAddons((current) =>
                  current.includes(addonValue)
                    ? current
                    : [...current, addonValue]
                );
              }
              break;
            }
            default: {
              const modifierId = modifier.modifierId as string;
              const sourceModifier = effectiveModifiers.find((mod) => mod._id === modifier.modifierId);
              if (sourceModifier && isModifierHandledByTemplate(template, sourceModifier.name)) {
                break;
              }
              const currentSelections = initialSelections.get(modifierId) ?? [];
              initialSelections.set(modifierId, [
                ...currentSelections,
                modifier.optionName,
              ]);
            }
          }
        }
      }

      setSelections(initialSelections);
    }
  }, [editingItem, effectiveModifiers, isEditing, item._id, open, pizzaSizeOptions, template]);

  // Track ViewContent event when sheet opens (for Meta Pixel)
  useEffect(() => {
    if (open && !preview) {
      analytics.menuItemViewed(item.name, item.price, item._id);
    }
  }, [open, item._id, item.name, item.price, preview]);

  // Price calculations
  const basePriceInCents = Math.round(item.price * 100);

  // Calculate modifier total from selections
  const modifierTotal = useMemo(() => {
    if (!filteredModifiers) return 0;
    let total = 0;

    for (const mod of filteredModifiers) {
      const selected = selections.get(mod._id) || [];
      for (const optName of selected) {
        const opt = mod.options.find((o) => o.name === optName);
        if (opt) total += opt.priceAdjustment;
      }
    }

    return total;
  }, [filteredModifiers, selections]);

  // Calculate category customization total
  const customizationTotal = useMemo(() => {
    let total = 0;

    // Add protein price if selected (for pasta/salad)
    if (selectedProtein) {
      const protein = PROTEIN_OPTIONS.find((p) => p.value === selectedProtein);
      if (protein) total += protein.price;
    }

    // Add brunch addon prices
    for (const addonValue of selectedBrunchAddons) {
      const addon = BRUNCH_ADDONS.find((a) => a.value === addonValue);
      if (addon) total += addon.price;
    }

    const selectedSize = pizzaSizeOptions.find(
      (option) => option.value === selectedPizzaSize,
    );
    if (selectedSize) {
      total += selectedSize.priceAdjustment;
      total +=
        selectedPizzaToppings.length *
        getPizzaToppingPriceForSize(selectedSize.value);
    }

    return total;
  }, [
    pizzaSizeOptions,
    selectedBrunchAddons,
    selectedPizzaSize,
    selectedPizzaToppings,
    selectedProtein,
  ]);

  const totalPrice = (basePriceInCents + modifierTotal + customizationTotal) * quantity;

  // Validate required groups
  const validation = useMemo(() => {
    const missingGroups: string[] = [];

    // Check temperature for steaks/burgers
    if (template === "temperature" && !temperature) {
      missingGroups.push("Temperature");
    }

    if (template === "pizza" && pizzaSizeOptions.length > 0 && !selectedPizzaSize) {
      missingGroups.push("Size");
    }

    // Check regular modifiers
    if (filteredModifiers) {
      for (const mod of filteredModifiers) {
        const selected = selections.get(mod._id) || [];

        // Check required
        if (mod.isRequired && selected.length === 0) {
          missingGroups.push(mod.name);
          continue;
        }

        // Check min selections
        if (mod.minSelections && selected.length < mod.minSelections) {
          missingGroups.push(mod.name);
        }
      }
    }

    return {
      valid: missingGroups.length === 0,
      missingGroups,
    };
  }, [template, temperature, pizzaSizeOptions, selectedPizzaSize, filteredModifiers, selections]);

  const imageUrl = item.photoUrl
    ? optimizeCloudinaryUrl(item.photoUrl, 600)
    : null;
  const priceLabel = formatMenuItemPriceLabel(item);

  const handleSelectionChange = (modifierId: string, options: string[]) => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(modifierId, options);
      return next;
    });
  };

  // Clear any pending pulse timer on unmount so we never set state after teardown.
  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, []);

  // On a blocked add-to-order tap, scroll the first unmet required group into view
  // and pulse it so the missing selection is the single, obvious signal.
  const handleBlockedSubmit = useCallback(() => {
    const firstMissing = validation.missingGroups[0];
    if (!firstMissing) return;

    const target = groupRefs.current.get(firstMissing);
    if (target) {
      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({
        behavior: prefersReduced ? "auto" : "smooth",
        block: "center",
      });
    }

    // Announce the missing selection to screen readers — the disabled CTA +
    // pulsing group are visual-only signals now that the red banner is gone.
    // Clear then set on the next frame so an identical message re-announces.
    const message =
      validation.missingGroups.length > 1
        ? "Please choose the required options to continue."
        : `Please select ${firstMissing} to continue.`;
    setBlockedMsg("");
    requestAnimationFrame(() => setBlockedMsg(message));

    setPulsingGroup(firstMissing);
    if (pulseTimeoutRef.current) {
      clearTimeout(pulseTimeoutRef.current);
    }
    pulseTimeoutRef.current = setTimeout(() => {
      setPulsingGroup(null);
    }, 1200);
  }, [validation.missingGroups]);

  const handleSubmit = () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    // Build modifiers array from selections
    const cartModifiers: CartItemModifier[] = [];

    // Add category-specific customizations as modifiers
    if (template === "temperature" && temperature) {
      cartModifiers.push({
        modifierId: "custom-temperature" as Id<"menu_modifiers">,
        modifierName: "Temperature",
        optionName: temperature,
        priceAdjustment: 0,
      });
    }

    if (template === "pasta") {
      if (sauceAmount !== "regular") {
        cartModifiers.push({
          modifierId: "custom-sauce" as Id<"menu_modifiers">,
          modifierName: "Sauce Amount",
          optionName: SAUCE_OPTIONS.find((o) => o.value === sauceAmount)?.label || sauceAmount,
          priceAdjustment: 0,
        });
      }
      if (selectedProtein) {
        const protein = PROTEIN_OPTIONS.find((p) => p.value === selectedProtein);
        if (protein) {
          cartModifiers.push({
            modifierId: "custom-protein" as Id<"menu_modifiers">,
            modifierName: "Add Protein",
            optionName: protein.label,
            priceAdjustment: protein.price,
          });
        }
      }
    }

    if (template === "pizza") {
      const selectedSize = pizzaSizeOptions.find(
        (option) => option.value === selectedPizzaSize,
      );
      if (selectedSize) {
        cartModifiers.push({
          modifierId: "custom-pizza-size" as Id<"menu_modifiers">,
          modifierName: "Size",
          optionName: selectedSize.label,
          priceAdjustment: selectedSize.priceAdjustment,
        });

        const toppingPrice = getPizzaToppingPriceForSize(selectedSize.value);
        for (const toppingValue of selectedPizzaToppings) {
          const topping = PIZZA_TOPPING_OPTIONS.find(
            (option) => option.value === toppingValue
          );
          if (topping) {
            cartModifiers.push({
              modifierId: `custom-pizza-topping-${topping.value}` as Id<"menu_modifiers">,
              modifierName: "Toppings",
              optionName: topping.label,
              priceAdjustment: toppingPrice,
            });
          }
        }
      }

      if (crustPreference !== "regular") {
        cartModifiers.push({
          modifierId: "custom-crust" as Id<"menu_modifiers">,
          modifierName: "Crust Preference",
          optionName: CRUST_OPTIONS.find((o) => o.value === crustPreference)?.label || crustPreference,
          priceAdjustment: 0,
        });
      }
    }

    if (template === "salad") {
      if (dressingPreference !== "regular") {
        cartModifiers.push({
          modifierId: "custom-dressing" as Id<"menu_modifiers">,
          modifierName: "Dressing",
          optionName: DRESSING_OPTIONS.find((o) => o.value === dressingPreference)?.label || dressingPreference,
          priceAdjustment: 0,
        });
      }
      if (selectedProtein) {
        const protein = PROTEIN_OPTIONS.find((p) => p.value === selectedProtein);
        if (protein) {
          cartModifiers.push({
            modifierId: "custom-protein" as Id<"menu_modifiers">,
            modifierName: "Add Protein",
            optionName: protein.label,
            priceAdjustment: protein.price,
          });
        }
      }
    }

    if (template === "brunch") {
      for (const addonValue of selectedBrunchAddons) {
        const addon = BRUNCH_ADDONS.find((a) => a.value === addonValue);
        if (addon) {
          cartModifiers.push({
            modifierId: "custom-brunch-addon" as Id<"menu_modifiers">,
            modifierName: "Add-on",
            optionName: addon.label,
            priceAdjustment: addon.price,
          });
        }
      }
    }

    // Add regular modifiers (use filteredModifiers to avoid duplicates with category customizers)
    if (filteredModifiers) {
      for (const mod of filteredModifiers) {
        const selected = selections.get(mod._id) || [];
        for (const optName of selected) {
          const opt = mod.options.find((o) => o.name === optName);
          if (opt) {
            cartModifiers.push({
              modifierId: mod._id,
              modifierName: mod.name,
              optionName: optName,
              priceAdjustment: opt.priceAdjustment,
            });
          }
        }
      }
    }

    const nextCartItem = {
      menuItemId: item._id,
      name: item.name,
      price: basePriceInCents,
      quantity,
      modifiers: cartModifiers,
      specialInstructions: specialInstructions.trim() || undefined,
      photoUrl: item.photoUrl || undefined,
    };

    if (preview) {
      preview.onAdd(nextCartItem);
      onOpenChange(false);
      return;
    }
    if (isEditing && editingItem) {
      replaceItem(editingItem.id, nextCartItem);
    } else {
      addItem(nextCartItem);
    }

    toast.success(
      isEditing
        ? `${item.name} updated`
        : `${quantity > 1 ? `${quantity}x ` : ""}${item.name} added to your order`,
      {
        description: formatPrice(totalPrice),
        duration: 2000,
      }
    );

    onOpenChange(false);
  };

  const isLoading = open && modifiers === undefined && !modifierLoadTimedOut;
  const optionsUnavailable = open && modifiers === undefined && modifierLoadTimedOut;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-menu-item-customization-sheet
        data-order-light-surface="true"
        className="z-[60] flex h-[100dvh] max-h-none flex-col rounded-none p-0 bg-background sm:inset-auto sm:h-[85vh] sm:max-h-[800px] sm:w-[95vw] sm:max-w-[1100px] sm:left-1/2 sm:right-auto sm:bottom-1/2 sm:translate-y-1/2 sm:-translate-x-1/2 sm:rounded-3xl overflow-hidden [&>button]:hidden shadow-2xl border-none ease-out data-[state=open]:duration-200 data-[state=closed]:duration-150"
      >
        <SheetDescription className="sr-only">
          Customize {item.name}, choose quantity, add notes, and add it to your order.
        </SheetDescription>
        {/* Screen-reader announcement for a blocked add-to-order tap. */}
        <div role="status" aria-live="assertive" className="sr-only">
          {blockedMsg}
        </div>
        <div className="flex flex-col h-full sm:flex-row overflow-hidden">
          {/* Persistent mobile close for photo items: the hero scrolls inside the
              content area, so a fixed control keeps a touch dismiss reachable at
              any scroll depth (photoless items use the sticky header row). */}
          {imageUrl && (
            <button
              onClick={() => onOpenChange(false)}
              className="fixed right-4 top-3 z-[70] flex h-11 w-11 items-center justify-center rounded-full bg-background/90 shadow-md backdrop-blur transition-transform active:scale-95 sm:hidden"
              aria-label="Close"
            >
              <XIcon className="h-5 w-5 text-foreground" />
            </button>
          )}
          {/* Desktop Left Sidebar: Square Image */}
          {imageUrl && (
            <div className="hidden sm:block sm:w-[55%] h-full relative bg-muted">
              <Image
                src={imageUrl}
                alt={item.name}
                fill
                className="object-cover"
                sizes="(min-width: 640px) 52vw, 100vw"
                priority
              />
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col h-full overflow-hidden bg-background">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
              {/* Mobile Hero - tall photo with floating close, or a compact close row when photoless */}
              {imageUrl ? (
                <div className="relative aspect-square w-full sm:hidden bg-muted">
                  <Image
                    src={imageUrl}
                    alt={item.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 639px) calc(100vw - 2rem), 0px"
                    priority
                  />
                  {/* Floating grabber handle */}
                  <div className="absolute inset-x-0 top-2 flex justify-center" aria-hidden="true">
                    <div className="h-1 w-12 rounded-full bg-foreground/30" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between px-4 pb-2 pt-2 flex-shrink-0 bg-background sticky top-0 z-50 sm:hidden">
                  <div className="flex-1 flex justify-center" aria-hidden="true">
                    <div className="h-1 w-12 rounded-full bg-muted" />
                  </div>
                  <button
                    onClick={() => onOpenChange(false)}
                    className="h-10 w-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 active:scale-95 transition-all shadow-sm"
                    aria-label="Close"
                  >
                    <XIcon className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>
              )}

              {/* Desktop Header - Desktop Only */}
              <div className="hidden sm:flex items-center justify-end px-4 pt-4 flex-shrink-0 bg-background">
                 <button
                  onClick={() => onOpenChange(false)}
                  className="h-9 w-9 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted active:scale-95 transition-all"
                  aria-label="Close"
                >
                  <XIcon className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>

              {/* Item Details Header */}
              <div className={cn(
                "px-4 sm:px-6 bg-background relative z-10",
                imageUrl ? "pt-3 pb-2 sm:pt-2 sm:pb-6" : "pt-3 pb-2 sm:pt-6 sm:pb-6"
              )}>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-col gap-0.5">
                    <SheetTitle className="text-xl font-bold leading-snug text-foreground tracking-tight text-balance sm:text-4xl sm:leading-tight">
                      {item.name}
                    </SheetTitle>
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {priceLabel}
                    </p>
                  </div>

                  {item.isPopular && (
                    <div>
                      <div className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded-full">
                        <Sparkles className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Guest Favorite</span>
                      </div>
                    </div>
                  )}

                  {item.description && (
                    <p className="text-sm text-muted-foreground leading-snug sm:mt-1 sm:text-base sm:leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Customization Options Area */}
              <MotionConfig reducedMotion="user">
              <div className="bg-background">
                {optionsUnavailable && (
                  <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium leading-5 text-amber-900 sm:px-6">
                    Some extra options are taking longer than usual. You can still add this item with standard preparation, or add a note for the kitchen.
                  </div>
                )}

                {/* Loading State — shape-matched skeleton so options reveal in place
                    instead of replacing a spinner with a layout shift. */}
                {isLoading && (
                  <div aria-hidden="true">
                    {[0, 1].map((group) => (
                      <div
                        key={group}
                        className="border-t border-border/50 px-4 py-4 sm:px-6 sm:py-5"
                      >
                        <div className="h-5 w-32 rounded-full bg-muted animate-pulse" />
                        <div className="mt-4 space-y-2">
                          {[0, 1, 2].map((row) => (
                            <div
                              key={row}
                              className="h-[60px] rounded-xl bg-muted animate-pulse"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Category-Specific Customizers */}
                {!isLoading && template === "temperature" && (
                  <div
                    ref={(el) => {
                      groupRefs.current.set("Temperature", el);
                    }}
                    className={cn(
                      "px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 bg-muted/30 scroll-mt-24 transition-all duration-300",
                      pulsingGroup === "Temperature" &&
                        "bg-destructive/5 ring-2 ring-inset ring-destructive/40"
                    )}
                  >
                    <TemperatureSelector
                      value={temperature}
                      onChange={setTemperature}
                      isRequired={true}
                    />
                  </div>
                )}

                {!isLoading && template === "pasta" && (
                  <div className="px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 bg-muted/30">
                    <PastaCustomizer
                      sauceAmount={sauceAmount}
                      onSauceChange={setSauceAmount}
                      selectedProtein={selectedProtein}
                      onProteinChange={setSelectedProtein}
                    />
                  </div>
                )}

                {!isLoading && template === "pizza" && (
                  <div
                    ref={(el) => {
                      groupRefs.current.set("Size", el);
                    }}
                    className={cn(
                      "px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 bg-muted/30 scroll-mt-24 transition-all duration-300",
                      pulsingGroup === "Size" &&
                        "bg-destructive/5 ring-2 ring-inset ring-destructive/40"
                    )}
                  >
                    <PizzaCustomizer
                      crustPreference={crustPreference}
                      onCrustChange={setCrustPreference}
                      sizeOptions={pizzaSizeOptions}
                      selectedSize={selectedPizzaSize}
                      onSizeChange={setSelectedPizzaSize}
                      selectedToppings={selectedPizzaToppings}
                      onToppingsChange={setSelectedPizzaToppings}
                      toppingPriceCents={
                        selectedPizzaSize
                          ? getPizzaToppingPriceForSize(selectedPizzaSize)
                          : null
                      }
                    />
                  </div>
                )}

                {!isLoading && template === "salad" && (
                  <div className="px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 bg-muted/30">
                    <SaladCustomizer
                      dressingPreference={dressingPreference}
                      onDressingChange={setDressingPreference}
                      selectedProtein={selectedProtein}
                      onProteinChange={setSelectedProtein}
                    />
                  </div>
                )}

                {!isLoading && template === "brunch" && (
                  <div className="px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 bg-muted/30">
                    <BrunchCustomizer
                      selectedAddons={selectedBrunchAddons}
                      onAddonsChange={setSelectedBrunchAddons}
                    />
                  </div>
                )}

                {/* Remaining Modifier Groups */}
                {filteredModifiers && filteredModifiers.length > 0 && (
                  <>
                    {filteredModifiers.map((modifier, index) => (
                      <div
                        key={modifier._id}
                        ref={(el) => {
                          groupRefs.current.set(modifier.name, el);
                        }}
                        className={cn(
                          "px-4 py-4 sm:px-6 sm:py-5 border-t border-border/50 scroll-mt-24 transition-all duration-300",
                          index % 2 === 0 ? "bg-background" : "bg-muted/20",
                          pulsingGroup === modifier.name &&
                            "bg-destructive/5 ring-2 ring-inset ring-destructive/40"
                        )}
                      >
                        <ModifierGroup
                          modifier={modifier}
                          selectedOptions={selections.get(modifier._id) || []}
                          onSelectionChange={(options) =>
                            handleSelectionChange(modifier._id, options)
                          }
                        />
                      </div>
                    ))}
                  </>
                )}

                <div className="border-t border-border/50 px-4 py-4 sm:px-6 sm:py-5">
                  <label
                    htmlFor="item-special-instructions"
                    className="text-sm font-semibold text-foreground"
                  >
                    Special instructions
                  </label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Optional. Allergies, swaps, or anything the kitchen should know.
                  </p>
                  <textarea
                    id="item-special-instructions"
                    value={specialInstructions}
                    onChange={(event) =>
                      setSpecialInstructions(event.target.value.slice(0, 500))
                    }
                    maxLength={500}
                    rows={3}
                    placeholder="No onions, extra napkins…"
                    className="mt-3 w-full resize-y rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                {/* Goes Well With Section — after modifiers so required choices stay primary */}
                {!isLoading && item.restaurantId !== "roma-kitchen" && !item.locationId?.startsWith("roma-kitchen-") && (
                  <div
                    className="px-4 py-4 sm:px-6 sm:py-6 border-t border-border/50 bg-muted/20"
                    data-menu-item-upsell
                  >
                    <GoesWellWithSection
                      category={item.category}
                      currentItemId={item._id}
                    />
                  </div>
                )}

                {/* Bottom spacing for scroll - the sticky footer already reserves space */}
                <div className="h-6 sm:h-10" />
              </div>
              </MotionConfig>
            </div>

            {/* Footer with Quantity & Add Button - Sticky bottom */}
            <div className="border-t border-border bg-background px-4 py-3 pb-[var(--sticky-bar-pb)] sm:px-5 sm:py-4 sticky bottom-0 z-50 shadow-[0_-8px_32px_-4px_rgba(0,0,0,0.15)] dark:shadow-[0_-8px_32px_-4px_rgba(0,0,0,0.4)]">
              <div className="flex items-center gap-3 sm:gap-4 w-full">
                {/* Quantity Selector */}
                <div className="flex items-center rounded-full border-2 border-border bg-background touch-manipulation">
                  <button
                    type="button"
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-l-full hover:bg-muted active:scale-95 transition-all touch-manipulation flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-5 w-5 text-foreground" />
                  </button>
                  <span className="w-9 sm:w-10 text-center text-lg font-bold tabular-nums text-foreground">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-r-full hover:bg-muted active:scale-95 transition-all touch-manipulation flex items-center justify-center"
                    onClick={() => setQuantity(quantity + 1)}
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-5 w-5 text-foreground" />
                  </button>
                </div>

                {/* Add to Order Button */}
                <button
                  type="button"
                  aria-label={
                    isLoading
                      ? "Loading"
                      : !validation.valid
                        ? validation.missingGroups.length > 1
                          ? "Choose required options to continue"
                          : `Select ${validation.missingGroups[0]} to continue`
                        : preview
                          ? `Add to preview cart, ${formatPrice(totalPrice)}`
                        : isEditing
                          ? "Save changes"
                          : `Add to order, ${formatPrice(totalPrice)}`
                  }
                  aria-disabled={!validation.valid || isLoading}
                  className={cn(
                    "flex-1 h-14 text-base font-bold rounded-full transition-all active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2",
                    !validation.valid || isLoading
                      ? "cursor-not-allowed bg-zinc-200 text-zinc-700"
                      : "bg-[var(--roma-terracotta)] text-white hover:bg-[var(--roma-saffron-deep)] shadow-[0_14px_42px_-22px_rgb(244_92_36_/_0.85)]"
                  )}
                  onClick={() => {
                    if (isLoading) return;
                    if (!validation.valid) {
                      handleBlockedSubmit();
                      return;
                    }
                    handleSubmit();
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading…</span>
                    </>
                  ) : !validation.valid ? (
                    <span>
                      {validation.missingGroups.length > 1
                        ? "Choose required options"
                        : `Select ${validation.missingGroups[0]}`}
                    </span>
                  ) : (
                    <>
                      <span>{preview ? "Add to preview cart" : isEditing ? "Save changes" : "Add to order"}</span>
                      <span aria-hidden="true">·</span>
                      <span className="font-bold">{formatPrice(totalPrice)}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
